import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { ArrowLeft, MapPin, Bed, Bath, Square, Calendar, Zap, ChevronLeft, ChevronRight, Phone, Mail, Layers, Ruler, Eye, Car, Fence, TreePine, ArrowUpDown, Warehouse, Maximize2, Share2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Lightbox } from '../components/Lightbox';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '', privacy: false });
  const [submitting, setSubmitting] = useState(false);
  const [geoCoords, setGeoCoords] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showAllThumbnails, setShowAllThumbnails] = useState(false);
  const [floorplanLightbox, setFloorplanLightbox] = useState(null);
  const [aiLightbox, setAiLightbox] = useState(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const { data } = await axios.get(`${API}/properties/${id}`);
        setProperty(data);
        // If no lat/lng, geocode from address
        if ((!data.latitude || !data.longitude) && data.address && data.city) {
          try {
            const geoRes = await axios.get(`${API}/geocode`, {
              params: { address: data.address, city: data.city }
            });
            if (geoRes.data.latitude && geoRes.data.longitude) {
              setGeoCoords({ lat: geoRes.data.latitude, lng: geoRes.data.longitude });
            }
          } catch (geoError) {
            console.error('Geocoding failed:', geoError);
          }
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  // Preload adjacent images (next + previous) as soon as the index changes,
  // so clicking "next" feels instant even when the proxy disables HTTP cache.
  useEffect(() => {
    if (!property?.images?.length) return;
    const total = property.images.length;
    const toPreload = new Set([
      (currentImageIndex + 1) % total,
      (currentImageIndex - 1 + total) % total,
      (currentImageIndex + 2) % total,
    ]);
    toPreload.forEach((idx) => {
      const img = new window.Image();
      img.decoding = 'async';
      img.src = `${API}/files/${property.images[idx]}?v=3`;
    });
  }, [currentImageIndex, property]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.privacy) {
      toast.error('Devi accettare la Privacy Policy per continuare');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        property_id: property.id,
        message: `Richiesta info per: ${property.title}\n\n${contactForm.message}`,
      });
      toast.success('Messaggio inviato con successo!');
      setContactForm({ name: '', email: '', phone: '', message: '', privacy: false });
    } catch (error) {
      toast.error("Errore nell'invio del messaggio");
    } finally {
      setSubmitting(false);
    }
  };

  // Galleria principale + lightbox: serviamo il file ORIGINALE (senza resize)
  // per mantenere la qualità massima che l'utente ha caricato. Il cache
  // HTTP + Cloudflare compensano il maggior peso in termini di velocità.
  const getImageUrl = (imagePath, size = 'large') => `${API}/files/${imagePath}?v=3`;
  const getImageUrlFull = (imagePath) => `${API}/files/${imagePath}?v=3`;

  const images = property?.images?.length > 0
    ? property.images.map((p) => getImageUrlFull(p))
    : ['https://images.unsplash.com/photo-1706808849827-7366c098b317?w=1200'];

  // Strip thumbnail sotto la galleria: piccole, usiamo la versione thumb
  const thumbImages = property?.images?.length > 0
    ? property.images.map((p) => `${API}/files/${p}?size=thumb&v=3`)
    : images;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Determine map coordinates
  const mapLat = property?.latitude || geoCoords?.lat;
  const mapLng = property?.longitude || geoCoords?.lng;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="property-detail-loading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
            <div className="aspect-[16/9] bg-gray-200 rounded-xl mb-8" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-10 bg-gray-200 rounded w-1/2" />
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-32 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen pt-32 pb-16 flex items-center justify-center" data-testid="property-not-found">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-text-main mb-4">Immobile non trovato</h1>
          <Link to="/properties" className="text-primary font-sans hover:underline">
            Torna agli annunci
          </Link>
        </div>
      </div>
    );
  }

  // Dynamic meta tags for share preview and browser tab title. The tags are
  // updated client-side by react-helmet-async so that when the URL is opened
  // in a browser, tab title + share sheet metadata reflect this property.
  // Note: crawlers that don't execute JS (WhatsApp/Facebook) still need to
  // hit /api/share/{id} for the correct preview.
  const shareUrl = `${API}/share/${property.id}`;
  const firstImgUrl = property.images?.length > 0
    ? `${API}/files/${property.images[0]}?v=3`
    : null;
  const metaTitle = `${property.title} — ${property.city} | Green House`;
  const metaDescription = property.description
    ? `${formatPrice(property.price)} · ${property.description.slice(0, 155)}`
    : `${formatPrice(property.price)} — ${property.property_type} a ${property.city}`;

  return (
    <div data-testid="property-detail-page" className="min-h-screen pt-24">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {firstImgUrl && <meta property="og:image" content={firstImgUrl} />}
        <meta property="og:url" content={shareUrl} />
        <meta property="og:site_name" content="Green House Servizi Immobiliari" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {firstImgUrl && <meta name="twitter:image" content={firstImgUrl} />}
      </Helmet>
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link
          to="/properties"
          className="inline-flex items-center text-text-muted hover:text-primary transition-colors font-sans text-sm"
          data-testid="back-to-properties"
        >
          <ArrowLeft size={18} className="mr-2" />
          Torna agli annunci
        </Link>
      </div>

      {/* Image Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8" data-testid="property-gallery">
        <div className="relative rounded-xl overflow-hidden">
          <div className="aspect-[16/9] md:aspect-[21/9] relative cursor-pointer bg-background-alt" onClick={() => setLightboxOpen(true)} data-testid="gallery-main-image">
            {/* Only render the 3 relevant images (current, prev, next) instead
                of all N photos. For annunci with 40-50 photos this prevents
                the browser from trying to download everything at once. */}
            {images.map((img, idx) => {
              const total = images.length;
              const isNearby =
                idx === currentImageIndex ||
                idx === (currentImageIndex + 1) % total ||
                idx === (currentImageIndex - 1 + total) % total;
              if (!isNearby) return null;
              return (
                <img
                  key={idx}
                  src={img}
                  alt={idx === currentImageIndex ? property.title : ''}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                  loading={idx === currentImageIndex ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={idx === currentImageIndex ? 'high' : 'low'}
                  draggable={false}
                />
              );
            })}
            <div className="absolute top-4 left-4 flex gap-2 z-10">
              <span className="bg-primary text-white px-4 py-2 rounded-md text-sm font-sans font-medium uppercase">
                {property.contract_type === 'vendita' ? 'In Vendita' : 'In Affitto'}
              </span>
              <span className="bg-white/90 text-text-main px-4 py-2 rounded-md text-sm font-sans font-medium">
                {property.property_type}
              </span>
            </div>
            {images.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 rounded-full hover:bg-white transition-colors z-10" data-testid="gallery-prev">
                  <ChevronLeft size={24} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 rounded-full hover:bg-white transition-colors z-10" data-testid="gallery-next">
                  <ChevronRight size={24} />
                </button>
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-md text-sm font-sans z-10">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Thumbnails - compact for large galleries */}
        {images.length > 1 && (
          <div className="mt-3">
            <div className={`flex gap-1.5 overflow-x-auto pb-2 ${!showAllThumbnails && images.length > 8 ? 'max-h-14' : ''}`}>
              {(showAllThumbnails ? thumbImages : thumbImages.slice(0, 8)).map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all ${
                    index === currentImageIndex ? 'border-primary ring-1 ring-primary' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
              {!showAllThumbnails && thumbImages.length > 8 && (
                <button
                  onClick={() => setShowAllThumbnails(true)}
                  className="flex-shrink-0 w-16 h-12 rounded-md bg-primary/10 border-2 border-transparent flex items-center justify-center hover:bg-primary/20 transition-colors"
                  data-testid="show-all-thumbnails"
                >
                  <span className="font-sans text-xs font-semibold text-primary">+{images.length - 8}</span>
                </button>
              )}
            </div>
            {showAllThumbnails && images.length > 8 && (
              <button
                onClick={() => setShowAllThumbnails(false)}
                className="mt-2 font-sans text-xs text-primary hover:text-primary-hover transition-colors"
                data-testid="hide-thumbnails"
              >
                Mostra meno
              </button>
            )}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={images}
          initialIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Price */}
            <div>
              <p className="font-serif text-4xl text-primary font-semibold mb-2">
                {formatPrice(property.price)}
                {property.contract_type === 'affitto' && <span className="text-lg">/mese</span>}
              </p>
              <h1 className="font-serif text-3xl md:text-4xl text-text-main mb-4">{property.title}</h1>
              <div className="flex items-center text-text-muted">
                <MapPin size={18} className="mr-2" />
                <span className="font-sans">{property.address}, {property.city}</span>
              </div>
              <div className="flex items-center text-text-muted mt-2">
                <Eye size={16} className="mr-2" />
                <span className="font-sans text-sm">{property.views || 0} visualizzazioni</span>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 py-6 border-y border-border">
              {property.rooms && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Bed size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Vani</span>
                  <span className="font-sans font-semibold text-text-main">{property.rooms}</span>
                </div>
              )}
              {property.bathrooms && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Bath size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Bagni</span>
                  <span className="font-sans font-semibold text-text-main">{property.bathrooms}</span>
                </div>
              )}
              <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                <Square size={22} className="text-primary mb-2" />
                <span className="font-sans text-sm text-text-muted">Interni</span>
                <span className="font-sans font-semibold text-text-main">{property.area_sqm} m²</span>
              </div>
              {property.external_area_sqm && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Ruler size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Esterni</span>
                  <span className="font-sans font-semibold text-text-main">{property.external_area_sqm} m²</span>
                </div>
              )}
              {property.floor !== null && property.floor !== undefined && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Layers size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Piano</span>
                  <span className="font-sans font-semibold text-text-main">{property.floor}</span>
                </div>
              )}
              {property.parking && property.parking !== 'none' && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Car size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Parcheggio</span>
                  <span className="font-sans font-semibold text-text-main text-center text-xs">
                    {property.parking.split(',').map(p => {
                      if (p === 'box_auto') {
                        return `Box Auto${property.box_auto_sqm ? ` (${property.box_auto_sqm} m²)` : ''}`;
                      }
                      if (p === 'posto_auto') {
                        return `Posto Auto${property.posto_auto_sqm ? ` (${property.posto_auto_sqm} m²)` : ''}`;
                      }
                      return p;
                    }).join(' + ')}
                  </span>
                </div>
              )}
              {property.has_elevator && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <ArrowUpDown size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Ascensore</span>
                  <span className="font-sans font-semibold text-text-main">Si</span>
                </div>
              )}
              {property.cellar && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Warehouse size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Cantinola</span>
                  <span className="font-sans font-semibold text-text-main">{property.cellar_sqm ? `${property.cellar_sqm} m²` : 'Si'}</span>
                </div>
              )}
              {(property.terrace_sqm !== null && property.terrace_sqm !== undefined) && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Fence size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Terrazzo</span>
                  <span className="font-sans font-semibold text-text-main">{property.terrace_sqm > 0 ? `${property.terrace_sqm} m²` : 'Si'}</span>
                </div>
              )}
              {(property.garden_sqm !== null && property.garden_sqm !== undefined) && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <TreePine size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Giardino</span>
                  <span className="font-sans font-semibold text-text-main">{property.garden_sqm > 0 ? `${property.garden_sqm} m²` : 'Si'}</span>
                </div>
              )}
              {property.year_built && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Calendar size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Anno</span>
                  <span className="font-sans font-semibold text-text-main">{property.year_built}</span>
                </div>
              )}
              {property.energy_class && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Zap size={22} className="text-primary mb-2" />
                  <span className="font-sans text-sm text-text-muted">Classe En.</span>
                  <span className="font-sans font-semibold text-text-main">{property.energy_class}</span>
                </div>
              )}
              {property.ipe_value && (
                <div className="flex flex-col items-center p-4 bg-background-alt rounded-xl">
                  <Zap size={22} className="text-accent mb-2" />
                  <span className="font-sans text-sm text-text-muted">IPE</span>
                  <span className="font-sans font-semibold text-text-main text-xs">{property.ipe_value} kWh/m²a</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="font-serif text-2xl text-text-main mb-4">Descrizione</h2>
              <p className="font-sans text-text-muted leading-relaxed whitespace-pre-line">
                {property.description}
              </p>
            </div>

            {/* Features List */}
            {property.features && property.features.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-text-main mb-4">Caratteristiche</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 font-sans text-text-muted">
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Planimetrie */}
            {property.floorplans && property.floorplans.length > 0 && (
              <div data-testid="property-floorplans">
                <h2 className="font-serif text-2xl text-text-main mb-4">Planimetrie</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {property.floorplans.map((plan, index) => (
                    <div
                      key={index}
                      className="rounded-xl overflow-hidden shadow-lg border border-border cursor-pointer hover:shadow-xl transition-shadow"
                      onClick={() => { setCurrentImageIndex(0); setFloorplanLightbox(index); }}
                    >
                      <img
                        src={getImageUrl(plan)}
                        alt={`Planimetria ${index + 1}`}
                        className="w-full h-auto object-contain bg-gray-50 max-h-[400px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Floorplan Lightbox */}
            {floorplanLightbox !== null && property.floorplans && (
              <Lightbox
                images={property.floorplans.map(getImageUrl)}
                initialIndex={floorplanLightbox}
                onClose={() => setFloorplanLightbox(null)}
              />
            )}

            {/* AI Generated Photos - dedicated section, only visible if any exists */}
            {property.ai_images && property.ai_images.length > 0 && (
              <div data-testid="property-ai-images">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <h2 className="font-serif text-xl text-text-main flex items-center gap-2 mb-2">
                        <Sparkles size={22} className="text-purple-500" />
                        Foto generate con Intelligenza Artificiale
                      </h2>
                      <p className="font-sans text-sm text-text-muted">
                        Visualizzazioni realizzate con IA per aiutarti a immaginare l'immobile
                        arredato, ristrutturato o valorizzato. Non rappresentano lo stato attuale.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setAiLightbox(0)}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                      data-testid="open-ai-gallery"
                    >
                      <Sparkles size={16} />
                      Guarda le {property.ai_images.length} foto IA
                    </Button>
                  </div>
                  {/* Small preview grid */}
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mt-4">
                    {property.ai_images.slice(0, 6).map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setAiLightbox(idx)}
                        className="relative aspect-square rounded-md overflow-hidden border border-purple-200 hover:border-purple-500 transition-colors group"
                        data-testid={`ai-preview-${idx}`}
                      >
                        <img
                          src={`${API}/files/${img}?size=thumb&v=3`}
                          alt={`Foto IA ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                        />
                        {idx === 5 && property.ai_images.length > 6 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-sans font-semibold">
                            +{property.ai_images.length - 6}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* AI Lightbox */}
            {aiLightbox !== null && property.ai_images && property.ai_images.length > 0 && (
              <Lightbox
                images={property.ai_images.map((p) => `${API}/files/${p}?v=3`)}
                initialIndex={aiLightbox}
                onClose={() => setAiLightbox(null)}
              />
            )}

            {/* Virtual Tour */}
            {property.virtual_tour_url && (
              <div data-testid="property-virtual-tour">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h2 className="font-serif text-2xl text-text-main">Virtual Tour 3D</h2>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const iframe = document.getElementById('virtual-tour-iframe');
                        if (iframe?.requestFullscreen) iframe.requestFullscreen();
                        else if (iframe?.webkitRequestFullscreen) iframe.webkitRequestFullscreen();
                        else if (iframe?.msRequestFullscreen) iframe.msRequestFullscreen();
                      }}
                      data-testid="virtual-tour-fullscreen"
                    >
                      <Maximize2 size={16} className="mr-2" />
                      Schermo intero
                    </Button>
                    <a
                      href={property.virtual_tour_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-sans text-primary hover:text-primary-hover underline"
                      data-testid="virtual-tour-new-tab"
                    >
                      Apri in nuova scheda
                    </a>
                  </div>
                </div>
                <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    id="virtual-tour-iframe"
                    src={property.virtual_tour_url}
                    title="Virtual Tour"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; fullscreen"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Video */}
            {property.video_url && (
              <div data-testid="property-video">
                <h2 className="font-serif text-2xl text-text-main mb-4">Video</h2>
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 shadow-lg">
                  <iframe
                    src={(() => {
                      let url = property.video_url;
                      // Convert standard YouTube URLs to embed format
                      if (url.includes('youtube.com/watch')) {
                        const videoId = new URL(url).searchParams.get('v');
                        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                      }
                      if (url.includes('youtu.be/')) {
                        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
                        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                      }
                      // Already embed or other format
                      return url;
                    })()}
                    title="Video immobile"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Map */}
            {mapLat && mapLng && (
              <div data-testid="property-map">
                <h2 className="font-serif text-2xl text-text-main mb-4">Posizione</h2>
                <div className="h-[350px] md:h-[400px] rounded-xl overflow-hidden shadow-lg">
                  <MapContainer
                    center={[mapLat, mapLng]}
                    zoom={15}
                    scrollWheelZoom={false}
                    className="h-full w-full"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[mapLat, mapLng]}>
                      <Popup>
                        <strong>{property.title}</strong><br />
                        {property.address}, {property.city}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Contact Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-28" data-testid="contact-form-sidebar">
              <h3 className="font-serif text-xl text-text-main mb-4">Richiedi Informazioni</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <Input
                  placeholder="Nome e Cognome"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  data-testid="contact-name"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  data-testid="contact-email"
                />
                <Input
                  type="tel"
                  placeholder="Telefono"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  required
                  data-testid="contact-phone"
                />
                <Textarea
                  placeholder="Il tuo messaggio..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={4}
                  required
                  data-testid="contact-message"
                />
                <label className="flex items-start gap-2 text-xs font-sans text-text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contactForm.privacy}
                    onChange={(e) => setContactForm({ ...contactForm, privacy: e.target.checked })}
                    required
                    className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                    data-testid="contact-privacy-checkbox"
                  />
                  <span>
                    Dichiaro di aver letto e accetto la{' '}
                    <a href="https://www.iubenda.com/privacy-policy/84083459" className="text-primary underline hover:text-primary-hover" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    {' '}(GDPR) *
                  </span>
                </label>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-hover"
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </form>

              {/* Share section with helpful explanation */}
              <div className="mt-4 border-t border-border pt-4" data-testid="share-section">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 size={16} className="text-primary" />
                  <p className="font-sans text-sm font-semibold text-text-main">Condividi questo annuncio</p>
                </div>
                <p className="font-sans text-xs text-text-muted mb-3">
                  Usa i pulsanti qui sotto: il link inviato mostrerà automaticamente foto, titolo e prezzo dell'immobile su WhatsApp e social.
                </p>

                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${property.title}\n${API}/share/${property.id}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-md py-2.5 font-sans font-medium transition-colors"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 size={18} />
                  Condividi su WhatsApp
                </a>

                {/* Copy shareable link only — no native share sheet */}
                <button
                  type="button"
                  onClick={async () => {
                    const linkToCopy = `${API}/share/${property.id}`;
                    try {
                      await navigator.clipboard.writeText(linkToCopy);
                      toast.success('Link copiato negli appunti');
                    } catch {
                      // Fallback for browsers that block clipboard API
                      const ta = document.createElement('textarea');
                      ta.value = linkToCopy;
                      document.body.appendChild(ta);
                      ta.select();
                      try {
                        document.execCommand('copy');
                        toast.success('Link copiato negli appunti');
                      } catch {
                        toast.error('Impossibile copiare il link');
                      }
                      document.body.removeChild(ta);
                    }
                  }}
                  className="mt-2 w-full flex items-center justify-center gap-2 border border-border hover:bg-background-alt text-text-main rounded-md py-2.5 font-sans text-sm transition-colors"
                  data-testid="copy-share-link-btn"
                >
                  <Share2 size={16} />
                  Copia link
                </button>
              </div>

              {/* Contact the agency via WhatsApp */}
              <a
                href={`https://wa.me/393755148808?text=${encodeURIComponent(`Salve, sono interessato all'immobile: ${property.title} — ${API}/share/${property.id}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#128C7E] hover:bg-[#0f6f65] text-white rounded-md py-2.5 font-sans font-medium transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contatta l'agenzia
              </a>

              <div className="mt-6 pt-6 border-t border-border space-y-3">
                <a href="tel:+3908231848006" className="flex items-center gap-3 text-text-muted hover:text-primary transition-colors font-sans">
                  <Phone size={18} />
                  0823 1848006
                </a>
                <a href="tel:+393292435449" className="flex items-center gap-3 text-text-muted hover:text-primary transition-colors font-sans">
                  <Phone size={18} />
                  +39 329 2435449
                </a>
                <a href="mailto:info@greenhousecaserta.it" className="flex items-center gap-3 text-text-muted hover:text-primary transition-colors font-sans">
                  <Mail size={18} />
                  info@greenhousecaserta.it
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
